import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [impersonation, setImpersonation] = useState(() => {
    const saved = localStorage.getItem('impersonation');
    return saved ? JSON.parse(saved) : null;
  });

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
      
      // Check if user has impersonated_by info
      if (response.data.impersonated_by && !impersonation) {
        // Restore impersonation state from token info
        const savedOriginalToken = localStorage.getItem('original_token');
        if (savedOriginalToken) {
          setImpersonation({
            originalToken: savedOriginalToken,
            originalUser: response.data.impersonated_by,
            impersonatedUser: {
              id: response.data.id,
              name: response.data.name,
              email: response.data.email,
              role: response.data.role
            }
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setToken(access_token);
      setUser(userData);
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const register = async (name, email, password, role = 'user', client_id = null) => {
    try {
      const response = await axios.post(`${API_URL}/auth/register`, {
        name,
        email,
        password,
        role,
        client_id
      });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setToken(access_token);
      setUser(userData);
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Registration failed' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('original_token');
    localStorage.removeItem('impersonation');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setImpersonation(null);
  };

  // Impersonation functions
  const impersonateClient = async (clientId) => {
    try {
      // Save the current token before impersonating
      const currentToken = localStorage.getItem('token');
      
      const response = await axios.post(`${API_URL}/auth/impersonate/${clientId}`);
      const { access_token, impersonated_user, client, original_user } = response.data;
      
      // Save original token for later restoration
      localStorage.setItem('original_token', currentToken);
      localStorage.setItem('token', access_token);
      
      // Save impersonation state
      const impersonationData = {
        originalToken: currentToken,
        originalUser: original_user,
        impersonatedUser: impersonated_user,
        client: client
      };
      localStorage.setItem('impersonation', JSON.stringify(impersonationData));
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setToken(access_token);
      setImpersonation(impersonationData);
      
      // Fetch the new user data
      await fetchUser();
      
      return { success: true, impersonatedUser: impersonated_user, client };
    } catch (error) {
      console.error('Impersonation failed:', error);
      return { success: false, error: error.response?.data?.detail || 'Impersonation failed' };
    }
  };

  const endImpersonation = async () => {
    try {
      // Call the end impersonation endpoint for logging
      await axios.post(`${API_URL}/auth/end-impersonation`).catch(() => {});
      
      // Restore the original token
      const originalToken = impersonation?.originalToken || localStorage.getItem('original_token');
      
      if (originalToken) {
        localStorage.setItem('token', originalToken);
        localStorage.removeItem('original_token');
        localStorage.removeItem('impersonation');
        
        axios.defaults.headers.common['Authorization'] = `Bearer ${originalToken}`;
        setToken(originalToken);
        setImpersonation(null);
        
        // Fetch the original user data
        await fetchUser();
        
        return { success: true };
      }
      
      return { success: false, error: 'No original token found' };
    } catch (error) {
      console.error('End impersonation failed:', error);
      return { success: false, error: error.response?.data?.detail || 'Failed to end impersonation' };
    }
  };

  const value = {
    user,
    loading,
    token,
    login,
    register,
    logout,
    isAuthenticated: !!user,
    isSuperAdmin: user?.role === 'superadmin',
    isAdmin: user?.role === 'admin',
    isUser: user?.role === 'user',
    // Impersonation
    impersonation,
    isImpersonating: !!impersonation,
    impersonateClient,
    endImpersonation
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
